import { Form } from "antd";
import { runInAction } from "mobx";
import { useCallback, useEffect, useMemo } from "react";
import { ECheckoutCustomerType } from "@devowl-wp/api-real-commerce";
import { useStores } from "../../../store/stores.js";
import { LOCAL_STORAGE_CHECKOUT_RESTORE_VALUES } from "../../../utils/checkoutRestoreFormStorage.js";
import { localStorageTest } from "../../../utils/localStorageTest.js";
import { COUPON_FORM_VALUE_KEY } from "../components/right/CouponInput.js";
const RESTORE_TTL_MS = 1000 * 60 * 60 * 48;
const CONSENT_API_DEFINITION = [
    "local",
    LOCAL_STORAGE_CHECKOUT_RESTORE_VALUES,
    "*"
];
/**
 * Translate a legacy payload to the current shape. Customer-type derives
 * from `companyName` / `vatId` (legacy `isInvoiceAddressGiven` covers
 * both "private with invoice address" and "company"); coupon scoping
 * relaxes to plan-only since the legacy shape has no `productId`.
 */ function migrateV1Shape(raw) {
    const { isInvoiceAddressGiven, ...rest } = raw;
    const hasCompanyMarker = !!(raw.companyName || raw.vatId);
    const customerType = isInvoiceAddressGiven && hasCompanyMarker ? "company" : "individual";
    const wantsInvoiceAddress = !!isInvoiceAddressGiven && customerType === "individual";
    return {
        ...rest,
        customerType,
        wantsInvoiceAddress
    };
}
/** Detect the legacy shape via the absence of `customerType` plus an explicit `isInvoiceAddressGiven` key. */ function isV1ShapedPayload(raw) {
    if (!raw || typeof raw !== "object") {
        return false;
    }
    const candidate = raw;
    return candidate.customerType === undefined && Object.prototype.hasOwnProperty.call(candidate, "isInvoiceAddressGiven");
}
const TERMS_LIKE_KEYS = [
    "isTermsAccepted",
    "isNewsletterSignUpAccepted",
    "exclusionOfRevocation",
    "noLegalAdviceContractWork",
    "dpaForPayedSupport"
];
/**
 * Surgically drop the `coupon` key from the persisted form cache without
 * touching any other captured field. Called from the Remove-coupon action
 * in the Order Summary: without this, a later visit would restore the
 * previously-saved coupon even though the buyer explicitly cleared it.
 */ function eraseMemorizedCoupon() {
    if (!localStorageTest()) {
        return;
    }
    const raw = localStorage.getItem(LOCAL_STORAGE_CHECKOUT_RESTORE_VALUES);
    if (!raw) {
        return;
    }
    try {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object" && "coupon" in parsed) {
            delete parsed.coupon;
            localStorage.setItem(LOCAL_STORAGE_CHECKOUT_RESTORE_VALUES, JSON.stringify(parsed));
        }
    } catch  {
    // Corrupt payload — safer to leave it than to overwrite with partial data.
    }
}
/**
 * Merge a partial identification snapshot into the persisted form cache.
 * Used by the Paddle `Checkout.Vat.Applied` sync so that when the buyer
 * clicks "Edit details" after adding a VAT number in the iframe, the
 * re-mounted form restores `customerType = company` with the VAT ID and
 * country pre-filled — even though the identification was reset in memory.
 *
 * Non-string/boolean/number values are ignored so a stray object from a
 * caller cannot corrupt the cache shape.
 */ function mergeIntoMemorized(partial) {
    if (!localStorageTest()) {
        return;
    }
    const raw = localStorage.getItem(LOCAL_STORAGE_CHECKOUT_RESTORE_VALUES);
    const existing = (()=>{
        if (!raw) {
            return {};
        }
        try {
            const parsed = JSON.parse(raw);
            return parsed && typeof parsed === "object" ? parsed : {};
        } catch  {
            return {};
        }
    })();
    const sanitized = {};
    for (const [key, value] of Object.entries(partial)){
        if (typeof value === "string" || typeof value === "boolean" || typeof value === "number") {
            sanitized[key] = value;
        }
    }
    localStorage.setItem(LOCAL_STORAGE_CHECKOUT_RESTORE_VALUES, JSON.stringify({
        ...existing,
        ...sanitized,
        updated: Date.now()
    }));
}
/**
 * Restores the identification form from two possible sources: the
 * `localStorage` cache (48 h TTL, same key as v1 so both shortcodes cross-
 * read) or a backend incomplete-checkout record (Paddle "recover checkout"
 * email link). Writes back to `localStorage` on every change (consent-gated).
 */ function useRecoverableFormV2({ overwriteCheckoutCountry }) {
    const isRestorableFromLocalStorage = useMemo(()=>localStorageTest(), []);
    const { checkoutV2Store } = useStores();
    const { transaction, product: { data: { productPlan: { id: productPlanId, product: { id: productId } } } }, formValues: sharedFormValues } = checkoutV2Store;
    const [form] = Form.useForm();
    const recoveredIncompleteCheckout = transaction?.recoveredIncompleteCheckout;
    const initialValues = {
        customerType: "individual",
        email: "",
        firstName: "",
        lastName: "",
        companyName: "",
        wantsInvoiceAddress: false,
        street: "",
        city: "",
        zip: "",
        country: "",
        vatId: "",
        isTermsAccepted: false,
        exclusionOfRevocation: false,
        noLegalAdviceContractWork: false,
        dpaForPayedSupport: false,
        isNewsletterSignUpAccepted: false
    };
    // Restore from localStorage. Legacy entries are migrated on first read
    // (and persisted back in the new shape via the effect below), so a
    // buyer carrying a stale legacy entry still lands on a pre-filled form.
    const rawRestored = isRestorableFromLocalStorage ? (()=>{
        try {
            return JSON.parse(localStorage.getItem(LOCAL_STORAGE_CHECKOUT_RESTORE_VALUES) || "{}");
        } catch  {
            return {};
        }
    })() : {};
    const isV1Migration = isV1ShapedPayload(rawRestored);
    const restored = isV1Migration ? migrateV1Shape(rawRestored) : rawRestored ?? {};
    const { updated = 0, productId: restoredProductId, productPlanId: restoredProductPlanId, coupon: restoredCoupon, ...restoredValues } = restored;
    Object.assign(initialValues, restoredValues);
    // Overlay consent/terms checkboxes from the in-memory bag so that when
    // the buyer clicks "Edit details" after Payment and the form re-mounts,
    // previously ticked boxes stay ticked. These keys are *never* written to
    // localStorage (see `TERMS_LIKE_KEYS` below) — returning days later in a
    // new tab must show empty boxes so consent is freshly given each session.
    for (const key of TERMS_LIKE_KEYS){
        const carried = sharedFormValues[key];
        if (typeof carried === "boolean") {
            initialValues[key] = carried;
        }
    }
    // Drop the restored coupon when it is expired or was captured against a
    // different product / plan. Both IDs are checked so a code entered
    // while shopping product A doesn't leak into a later visit for product
    // B (plan IDs are globally unique today, but the explicit pair-check
    // defends against future keyspace overlap). Legacy migrations relax
    // to plan-only because the legacy shape never persisted `productId`.
    const couponMatchesProduct = restoredProductId === undefined ? true : String(restoredProductId) === String(productId);
    const couponValidForThisPlan = updated > 0 && couponMatchesProduct && String(restoredProductPlanId) === String(productPlanId) && Date.now() - updated <= RESTORE_TTL_MS;
    const couponToSeed = couponValidForThisPlan ? restoredCoupon : undefined;
    // Seed the restored coupon synchronously during render so the right-column
    // OrderSummary observes it in its first render — a `useEffect` here would
    // land one paint late and flash the "Got a coupon?" trigger before
    // revealing the pre-filled field. Query-param coupons already land in the
    // bag earlier (useCheckoutV2Transaction), so they always win.
    if (couponToSeed && !sharedFormValues[COUPON_FORM_VALUE_KEY]) {
        runInAction(()=>{
            sharedFormValues[COUPON_FORM_VALUE_KEY] = couponToSeed;
        });
    }
    if (overwriteCheckoutCountry) {
        initialValues.country = overwriteCheckoutCountry;
    }
    // After a legacy migration, persist the new-shape payload back so
    // subsequent reads bypass the shim. `productId` stays undefined (the
    // legacy payload didn't carry it), the coupon-scope fallback above
    // covers that case. Fire-and-forget — if consent is denied, the next
    // visit re-runs the same migration (idempotent).
    useEffect(()=>{
        if (!isV1Migration || !isRestorableFromLocalStorage) {
            return;
        }
        const writeBack = {
            ...restoredValues,
            coupon: couponToSeed,
            productPlanId: restoredProductPlanId,
            updated: updated > 0 ? updated : Date.now()
        };
        const { consentApi } = window;
        const waitConsent = consentApi?.consent(...CONSENT_API_DEFINITION) || Promise.resolve();
        waitConsent.then(()=>{
            localStorage.setItem(LOCAL_STORAGE_CHECKOUT_RESTORE_VALUES, JSON.stringify(writeBack));
        }).catch(()=>{
        // Swallow consent rejections — the migration runs again next visit.
        });
    // Once-per-mount; an empty dep list prevents replay on unrelated re-renders.
    }, []);
    // Apply initial values from a recovered (email-link) incomplete checkout.
    useEffect(()=>{
        if (recoveredIncompleteCheckout) {
            const { firstName, lastName, email, country, zip, street, city, companyName, vatId, paddlePayLink, customerType } = recoveredIncompleteCheckout.checkout;
            form.setFieldsValue({
                customerType: customerType === ECheckoutCustomerType.Company ? "company" : "individual",
                wantsInvoiceAddress: !!street,
                companyName: companyName ?? "",
                firstName: firstName ?? "",
                lastName: lastName ?? "",
                email: email ?? "",
                street: street ?? "",
                zip: zip ?? "",
                city: city ?? "",
                country: paddlePayLink?.overwriteCheckoutCountry || country || "",
                vatId: vatId ?? ""
            });
        }
    }, [
        recoveredIncompleteCheckout
    ]);
    /** Clear the `localStorage` cache — called after a successful submit to protect shared devices. */ const eraseMemorizedValues = useCallback(()=>{
        if (isRestorableFromLocalStorage) {
            localStorage.removeItem(LOCAL_STORAGE_CHECKOUT_RESTORE_VALUES);
        }
    }, [
        isRestorableFromLocalStorage
    ]);
    /**
     * Persist restorable fields. Terms-style acceptances are stripped from
     * localStorage (fresh consent per session), but mirrored to the in-memory
     * store bag so a same-session form re-mount (Edit Details) keeps them.
     */ const makeRestorable = useCallback((values, coupon)=>{
        runInAction(()=>{
            for (const key of TERMS_LIKE_KEYS){
                const value = values[key];
                if (typeof value === "boolean") {
                    sharedFormValues[key] = value;
                }
            }
        });
        if (!isRestorableFromLocalStorage) {
            return;
        }
        const restorable = {
            ...values,
            coupon
        };
        for (const key of TERMS_LIKE_KEYS){
            delete restorable[key];
        }
        const { consentApi } = window;
        const waitConsent = consentApi?.consent(...CONSENT_API_DEFINITION) || Promise.resolve();
        waitConsent.then(()=>{
            localStorage.setItem(LOCAL_STORAGE_CHECKOUT_RESTORE_VALUES, JSON.stringify({
                ...restorable,
                updated: Date.now(),
                productId,
                productPlanId
            }));
        }).catch(()=>{
        // Swallow consent rejections — persisting is best-effort.
        });
    }, [
        isRestorableFromLocalStorage,
        productId,
        productPlanId,
        sharedFormValues
    ]);
    return {
        recoveredIncompleteCheckout,
        initialValues,
        couponToSeed,
        form,
        eraseMemorizedValues,
        makeRestorable
    };
}
export { useRecoverableFormV2, eraseMemorizedCoupon, mergeIntoMemorized };
